/**
 * Editable draft over an `AdminUserRecord` (AR-327).
 *
 * The editor spans four tabs, so the draft lives once on the page rather than
 * per tab — switching tabs must not discard typing. Keys are flat dotted paths
 * (`profile.first`, `billing.zip`) so a tab can address a field without
 * knowing which table it came from.
 *
 * AR-382: when the record carries the `migrated` block (the `proMigration`
 * flag is on), fields seed from the migrated columns, only the server's
 * `writable` keys stay editable, and `changes()` is the Save payload — the
 * changed keys only, so an untouched field is never rewritten. Without the
 * block nothing persists, as in AR-327.
 */
import { useCallback, useMemo, useState } from 'react';
import type { AdminUserRecord } from '@/lib/adminUserRecordApi';
import { externalIdFor, primaryTramsAccount } from '@/lib/adminUserRecordApi';

type DraftValue = string | boolean | string[];
export type DraftState = Record<string, DraftValue>;

export interface RecordDraft {
  text: (key: string) => string;
  bool: (key: string) => boolean;
  list: (key: string) => string[];
  set: (key: string, value: DraftValue) => void;
  setText: (key: string) => (value: string) => void;
  setListItem: (key: string, index: number, value: string) => void;
  appendListItem: (key: string, value: string) => void;
  dirty: boolean;
  reset: () => void;
  /** True when the record is bound to the migrated model and can be saved. */
  bound: boolean;
  /** False for a field the bound record cannot save — render it read-only. */
  editable: (key: string) => boolean;
  /** Changed writable keys → values. An address goes whole when any line changed. */
  changes: () => Record<string, string | boolean | null>;
}

const STATUS_LABELS: Record<string, string> = {
  checkout_pending: 'Checkout Pending',
  pending: 'Pending',
  active: 'Active',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
  training: 'Training',
};

/**
 * The Account Status label the editor shows. Bound to the migrated model it
 * is `users.account_status` (AR-330); otherwise `users.onboarding_status`.
 */
export const accountStatusLabel = (record: AdminUserRecord): string => {
  const migrated = record.migrated?.user.accountStatus;
  if (migrated && STATUS_LABELS[migrated]) return STATUS_LABELS[migrated];
  const raw = record.user.onboardingStatus;
  if (raw && STATUS_LABELS[raw]) return STATUS_LABELS[raw];
  return record.user.isActive ? 'Active' : 'Deactivated';
};

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/** Flattens the record into the field values the four tabs bind to. */
export function draftFromRecord(record: AdminUserRecord): DraftState {
  const { user, profile, addresses, team } = record;
  const trams = primaryTramsAccount(record);
  const nameParts = user.displayName.trim().split(/\s+/);

  return {
    'user.email': user.email,
    'user.roleSlug': str(user.roleSlug ?? user.role),
    'user.accountStatus': accountStatusLabel(record),
    'user.isTestAccount': false,

    'profile.first': str(profile?.firstName ?? nameParts[0]),
    'profile.last': str(profile?.lastName ?? nameParts.slice(1).join(' ')),
    'profile.nickname': str(profile?.displayName ?? user.displayName),
    'profile.legalName': str(user.fullLegalName),
    'profile.salutation': str(profile?.salutation),
    'profile.employment': '',
    'profile.contactType': user.parentUserId ? 'Associate Advisor' : 'Agency Owner',
    'profile.tshirtSize': '',
    'profile.timezone': str(profile?.timezone),
    'profile.phone': str(profile?.phone ?? user.phone),
    'profile.mobilePhone': '',
    'profile.advisorShowcase': '',
    'profile.showcasePublished': true,
    'profile.worldviaWebsite': '',
    'profile.website': str(profile?.website),
    'profile.facebook': str(profile?.facebookPage),
    'profile.instagram': str(profile?.instagramHandle),
    'profile.linkedin': str(profile?.linkedinProfile),
    'profile.twitter': str(profile?.twitterHandle),
    'profile.websites': profile?.website ? [String(profile.website)] : [],

    'billing.street': str(addresses.billing?.street),
    'billing.line2': str(addresses.billing?.line2),
    'billing.city': str(addresses.billing?.city),
    'billing.state': str(addresses.billing?.state),
    'billing.zip': str(addresses.billing?.zip),

    'mailing.street': str(addresses.mailing?.street ?? addresses.billing?.street),
    'mailing.line2': str(addresses.mailing?.line2),
    'mailing.city': str(addresses.mailing?.city ?? profile?.city),
    'mailing.state': str(addresses.mailing?.state ?? profile?.state),
    'mailing.zip': str(addresses.mailing?.zip ?? profile?.zipCode),

    'ids.hubspot': str(externalIdFor(record, 'hubspot_contact')),
    'ids.tramsInterface': str(trams?.interfaceId),
    'ids.tramsAgentName': str(trams?.agentName),
    'ids.tramsProfileId': str(trams?.profileId),
    'ids.tramsCorporateId': str(trams?.corporateId),
    'ids.gdsUser': user.gdsInterest ? 'Multiple' : 'No',
    'ids.trioEpr': '',
    'ids.trioEnabled': false,

    'account.membershipPlan': str(record.plan?.name),
    'account.gemStatus': str(profile?.gemStatus),
    'account.legalEntityName': str(team?.agencyLegalEntityName),
    'account.brandName': str(team?.brandName ?? team?.name ?? profile?.businessName),
    'account.ein': '',
    'account.referredBy': str(user.referredBy),
  };
}

/** AR-382 · the same keys, seeded from the migrated columns. */
function boundDraftFromRecord(record: AdminUserRecord): DraftState {
  const base = draftFromRecord(record);
  const m = record.migrated;
  if (!m) return base;
  const { profile, addresses, team } = record;
  const duda = m.websites.find((site) => site.provider === 'duda');

  return {
    ...base,
    'user.isTestAccount': m.user.isTestAccount,
    'profile.nickname': str(m.profile.nickname),
    'profile.legalName': str(m.user.fullLegalName),
    'profile.employment': str(m.profile.employmentType),
    'profile.contactType': str(m.contactType ?? base['profile.contactType']),
    'profile.tshirtSize': str(m.profile.tshirtSize),
    'profile.mobilePhone': str(m.profile.secondaryPhone),
    'profile.advisorShowcase': m.profile.slug ? `worldvia.com/advisors/${m.profile.slug}` : '',
    'profile.showcasePublished': m.profile.showcasePublishState === 'published',
    'profile.worldviaWebsite': str(duda?.publicUrl),
    'profile.websites': m.websites
      .map((site) => site.publicUrl)
      .filter((url): url is string => Boolean(url)),
    // No billing fallback: a mailing row of its own is what gets saved, and
    // seeding it from billing would write billing's street on first edit.
    'mailing.street': str(addresses.mailing?.street),
    'ids.gdsUser': '',
    'ids.trioEpr': str(m.trio?.eprId),
    'ids.trioEnabled': m.trio?.isEnabled === true,
    'account.legalEntityName': str(m.profile.companyLegalName ?? team?.agencyLegalEntityName),
    'account.brandName': str(
      m.profile.companyBrandName ?? team?.brandName ?? team?.name ?? profile?.businessName,
    ),
    'account.ein': str(m.profile.companyEin),
    'account.referredBy': str(m.user.referredBy),
  };
}

const ADDRESS_LINES = ['street', 'line2', 'city', 'state', 'zip'];

export function useRecordDraft(record: AdminUserRecord | undefined): RecordDraft {
  // A refetch reseeds the baseline; anything the admin has typed stays on top.
  const initial = useMemo(() => (record ? boundDraftFromRecord(record) : {}), [record]);
  const [overrides, setOverrides] = useState<DraftState>({});
  const writable = useMemo(() => new Set(record?.migrated?.writable ?? []), [record]);
  const bound = Boolean(record?.migrated);

  const value = useCallback(
    (key: string): DraftValue | undefined =>
      key in overrides ? overrides[key] : initial[key],
    [initial, overrides],
  );

  const set = useCallback((key: string, next: DraftValue) => {
    setOverrides((prev) => ({ ...prev, [key]: next }));
  }, []);

  const list = useCallback(
    (key: string): string[] => {
      const current = value(key);
      return Array.isArray(current) ? current : [];
    },
    [value],
  );

  return useMemo(
    () => ({
      text: (key: string) => {
        const current = value(key);
        if (typeof current === 'string') return current;
        if (typeof current === 'boolean') return current ? 'true' : 'false';
        return '';
      },
      bool: (key: string) => value(key) === true,
      list,
      set,
      setText: (key: string) => (next: string) => set(key, next),
      setListItem: (key: string, index: number, next: string) =>
        setOverrides((prev) => {
          const current = Array.isArray(prev[key])
            ? [...(prev[key] as string[])]
            : [...(Array.isArray(initial[key]) ? (initial[key] as string[]) : [])];
          current[index] = next;
          return { ...prev, [key]: current };
        }),
      appendListItem: (key: string, next: string) =>
        setOverrides((prev) => {
          const current = Array.isArray(prev[key])
            ? [...(prev[key] as string[])]
            : [...(Array.isArray(initial[key]) ? (initial[key] as string[]) : [])];
          return { ...prev, [key]: [...current, next] };
        }),
      dirty: Object.keys(overrides).length > 0,
      reset: () => setOverrides({}),
      bound,
      editable: (key: string) => !bound || writable.has(key),
      changes: () => {
        const out: Record<string, string | boolean | null> = {};
        Object.entries(overrides).forEach(([key, next]) => {
          if (!writable.has(key) || Array.isArray(next) || next === initial[key]) return;
          const [group] = key.split('.');
          if (group === 'billing' || group === 'mailing') {
            ADDRESS_LINES.forEach((line) => {
              const lineKey = `${group}.${line}`;
              const current = value(lineKey);
              out[lineKey] = typeof current === 'string' ? current : '';
            });
          } else {
            out[key] = next;
          }
        });
        return out;
      },
    }),
    [bound, initial, list, overrides, set, value, writable],
  );
}
